import {
  Accordion,
  Button,
  ComponentType,
  FormSelect,
  FormTextField,
  Grid,
  Icon,
  IconName,
  Label,
  Popover,
  requiredRule,
  ToastMessage,
  toastMessage,
  Tooltip,
  UseFormReturn,
} from '@gcl/react-core';
import * as React from 'react';
import './DesignBriefDetailPage.scss';
import {
  useCreateAndIssueDesignBriefToTwc,
  useCreateDesignBrief,
  useCreateDesignBriefRevision,
  useFetchDesignBrief,
  useUpdateAndIssueDesignBriefToTwc,
  useUpdateAndIssueDesignBriefToTwd,
  useUpdateDesignBrief,
} from '@designBrief/designBrief.queries';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import { useNavigate } from '@routes/routes.hooks';
import { Attachments } from '@file/Attachments';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { useFetchAttachmentDesignBrief } from '@file/projectFile/projectFile.queries';
import { downloadAttachmentsAsync } from '@file/projectFile/projectFile.services';
import { SignatureCard } from '@common/SignatureCard';
import { FormTextareaWithMaxLength } from '@core/FormTextareaWithMaxLength';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { Note } from '@core/Note/Note';
import { ERiskCategory } from '@riskCategory/riskCategory.constants';
import { IProjectFileAttachmentsDesignBriefsRequest } from '@file/projectFile/projectFile.models';
import { FETCH_QUERIES_NAME } from '@common/constants';
import {
  IDesignBriefInfoAttached,
  IProjectDesignBriefInfoAttached,
} from '@designBrief/infoAttached/infoAttached.models';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useQueryClient } from '@tanstack/react-query';
import { EDesignBriefStatus } from '@designBrief/designBrief.constants';
import { convertUtcDateToTimezoneString } from '@core/date.services';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { routes } from '@routes/routes.models';
import { useT2Visibility } from '@designBrief/designBrief.hooks';
import { DownloadButton } from '@file/DownloadButton';
import { downloadDesignBriefPdfAsync } from '@designBrief/designBrief.services';
import { useDeferLoading } from '@common/loading.hooks';
import { useProjectIsArchived } from '@config/config.hooks';
import {
  DesignBriefDetailButtonAction,
  DesignBriefDetailButtonActionIssueToTwc,
  DesignBriefDetailButtonActionIssueToTwd,
  DesignBriefDetailButtonActionSave,
  EDesignBriefFormKeys,
  ICreateDesignBriefRequest,
  ICreateDesignBriefRevisionRequest,
  IUpdateDesignBriefRequest,
} from './designBriefDetailPage.models';
import { DesignBriefDetailInfoAttached } from './DesignBriefDetailInfoAttached';

interface IDesignBriefDetailTabProps {
  useFormMethods: UseFormReturn;
  isReadOnly: boolean;
  designBriefId?: number;
  masterScheduleId?: number;
  masterSchedule?: IMasterScheduleDetail;
  mode: DetailPageAccessMode;
  projectId: number;
  infoAttachedOptions?: IDesignBriefInfoAttached[];
}

export const DesignBriefDetailTab: React.FunctionComponent<IDesignBriefDetailTabProps> = ({
  useFormMethods,
  isReadOnly,
  designBriefId,
  masterScheduleId,
  masterSchedule,
  mode,
  projectId,
  infoAttachedOptions,
}) => {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = React.useState(false);
  const { navigate } = useNavigate();
  const isProjectArchived = useProjectIsArchived();

  const {
    formState: { isDirty },
  } = useFormMethods;

  const {
    data: designBriefDetail,
    isLoading: isLoadingDesignBrief,
    isFetching: isFetchingDesignBrief,
  } = useFetchDesignBrief({ projectId }, designBriefId, !isDirty);

  const { isAllowNewT2AndIssueToTwc, isAllowIssueToTwd, isAllowCreateRevision, modeAttachment } = useT2Visibility(
    masterSchedule,
    designBriefDetail,
    mode,
  );

  const {
    mutate: mutateCreate,
    isLoading: isLoadingCreate,
    isError: isErrorCreate,
    data: dataCreate,
    isSuccess: isSuccessCreate,
    error: errorCreate,
    reset: resetCreate,
  } = useCreateDesignBrief();

  const {
    mutate: mutateCreateAndIssueToTwc,
    isLoading: isLoadingCreateAndIssueToTwc,
    isError: isErrorCreateAndIssueToTwc,
    data: dataCreateAndIssueToTwc,
    isSuccess: isSuccessCreateAndIssueToTwc,
    error: errorCreateAndIssueToTwc,
    reset: resetCreateAndIssueToTwc,
  } = useCreateAndIssueDesignBriefToTwc();

  const {
    mutate: mutateCreateRevision,
    isLoading: isLoadingCreateRevision,
    isError: isErrorCreateRevision,
    data: dataCreateRevision,
    isSuccess: isSuccessCreateRevision,
    error: errorCreateRevision,
    reset: resetCreateRevision,
  } = useCreateDesignBriefRevision();

  const {
    mutate: mutateUpdate,
    isLoading: isLoadingUpdate,
    isError: isErrorUpdate,
    data: dataUpdate,
    isSuccess: isSuccessUpdate,
    error: errorUpdate,
    reset: resetUpdate,
  } = useUpdateDesignBrief();

  const {
    mutate: mutateUpdateAndIssueToTwc,
    isLoading: isLoadingUpdateAndIssueToTwc,
    isError: isErrorUpdateAndIssueToTwc,
    data: dataUpdateAndIssueToTwc,
    isSuccess: isSuccessUpdateAndIssueToTwc,
    error: errorUpdateAndIssueToTwc,
    reset: resetUpdateAndIssueToTwc,
  } = useUpdateAndIssueDesignBriefToTwc();

  const {
    mutate: mutateUpdateAndIssueToTwd,
    isLoading: isLoadingUpdateAndIssueToTwd,
    isError: isErrorUpdateAndIssueToTwd,
    data: dataUpdateAndIssueToTwd,
    isSuccess: isSuccessUpdateAndIssueToTwd,
    error: errorUpdateAndIssueToTwd,
    reset: resetUpdateAndIssueToTwd,
  } = useUpdateAndIssueDesignBriefToTwd();

  const { loading: isAnyActionLoading, setLoading: setIsAnyActionLoading } = useDeferLoading(
    isLoadingCreate ||
      isLoadingCreateAndIssueToTwc ||
      isLoadingCreateRevision ||
      isLoadingUpdate ||
      isLoadingUpdateAndIssueToTwc ||
      isLoadingUpdateAndIssueToTwd,
  );

  const handleSubmit = React.useCallback(
    (data: any, action: DesignBriefDetailButtonAction) => {
      if (data.attachments?.length === 0) {
        toastMessage(
          <ToastMessage
            message="T2 form may not be issued without attachment attached. Please ensure that attachments are available before raising T2 forms."
            toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
          />,
        );
        return;
      }
      const informationAttached: IProjectDesignBriefInfoAttached[] = data.informationAttached ?? [];

      if (mode === 'Create' && action === DesignBriefDetailButtonActionSave) {
        const createRequest: ICreateDesignBriefRequest = {
          ...data,
          carryOutDesignerCompany: data.designer.name,
          carryOutDesignerCompanyId: data.designer.id,
          informationAttached,
          projectId,
          projectMasterScheduleId: masterScheduleId,
        };
        mutateCreate(createRequest);
        return;
      }
      if (mode === 'Create' && action === DesignBriefDetailButtonActionIssueToTwc) {
        const createRequest: ICreateDesignBriefRequest = {
          ...data,
          carryOutDesignerCompany: data.designer.name,
          carryOutDesignerCompanyId: data.designer.id,
          informationAttached,
          projectId,
          projectMasterScheduleId: masterScheduleId,
        };
        mutateCreateAndIssueToTwc(createRequest);
        return;
      }

      if (mode === 'Update' && action === DesignBriefDetailButtonActionSave) {
        const updateRequest: IUpdateDesignBriefRequest = {
          ...data,
          id: designBriefDetail?.id,
          informationAttached,
          projectId,
          projectMasterScheduleId: masterScheduleId,
          rowVersion: designBriefDetail?.rowVersion,
        };
        mutateUpdate(updateRequest);
        return;
      }

      if (mode === 'Update' && action === DesignBriefDetailButtonActionIssueToTwc) {
        const updateRequest: IUpdateDesignBriefRequest = {
          ...data,
          id: designBriefDetail?.id,
          informationAttached,
          projectId,
          projectMasterScheduleId: masterScheduleId,
          rowVersion: designBriefDetail?.rowVersion,
        };
        mutateUpdateAndIssueToTwc(updateRequest);
        return;
      }

      if (mode === 'Update' && action === DesignBriefDetailButtonActionIssueToTwd) {
        const updateRequest: IUpdateDesignBriefRequest = {
          ...data,
          id: designBriefDetail?.id,
          informationAttached,
          projectId,
          projectMasterScheduleId: masterScheduleId,
          rowVersion: designBriefDetail?.rowVersion,
        };
        mutateUpdateAndIssueToTwd(updateRequest);
        return;
      }
      throw new Error('Invalid action.');
    },
    [mode, JSON.stringify(designBriefDetail)],
  );

  const onClickSave = () => {
    useFormMethods.handleSubmit((data: any) => handleSubmit(data, DesignBriefDetailButtonActionSave))();
  };
  const onClickIssueTwc = () => {
    useFormMethods.handleSubmit((data: any) => handleSubmit(data, DesignBriefDetailButtonActionIssueToTwc))();
  };
  const onClickIssueTwd = () => {
    useFormMethods.handleSubmit((data: any) => handleSubmit(data, DesignBriefDetailButtonActionIssueToTwd))();
  };
  const onClickCreateRevision = () => {
    const createRevisionRequest: ICreateDesignBriefRevisionRequest = {
      id: designBriefDetail?.id as number,
      projectId,
      projectMasterScheduleId: designBriefDetail?.projectMasterScheduleId as number,
    };
    mutateCreateRevision(createRevisionRequest);
  };

  const topBarLeftItems: IDetailPageTopBarItem[] = React.useMemo(() => {
    const items: IDetailPageTopBarItem[] = [];

    if (
      isAllowNewT2AndIssueToTwc &&
      designBriefDetail?.status !== EDesignBriefStatus.FINISHED &&
      !masterSchedule?.isCancelled
    ) {
      items.push({
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Primary,
        isError: isErrorCreate || isErrorUpdate,
        isLoading: mode === 'Create' ? isLoadingCreate : isLoadingUpdate,
        isSuccess: isSuccessCreate || isSuccessUpdate,
        title: 'Save',
        buttonIconName: 'FolderCheck',
        onClick: onClickSave,
        disable: isUploading || isAnyActionLoading,
        showNoneInReadOnly: true,
      });
    }

    if (
      !masterSchedule?.isCancelled &&
      ((isAllowNewT2AndIssueToTwc && !designBriefDetail?.status) ||
        designBriefDetail?.status === EDesignBriefStatus.DRAFT)
    ) {
      items.push({
        type: EDetailPageTopBarItemType.loadingButton,
        isError: isErrorCreateAndIssueToTwc || isErrorUpdateAndIssueToTwc,
        isLoading: mode === 'Create' ? isLoadingCreateAndIssueToTwc : isLoadingUpdateAndIssueToTwc,
        isSuccess: isSuccessCreateAndIssueToTwc || isSuccessUpdateAndIssueToTwc,
        title: 'Issue to TWC',
        buttonIconName: 'EnvelopeCheck',
        componentType: ComponentType.Success,
        onClick: onClickIssueTwc,
        disable: isUploading || isAnyActionLoading,
        showNoneInReadOnly: true,
      });
    }
    if (
      isAllowIssueToTwd &&
      designBriefDetail?.status === EDesignBriefStatus.SUBMITTED &&
      !designBriefDetail?.isTwcReceived &&
      !masterSchedule?.isCancelled
    ) {
      items.push({
        type: EDetailPageTopBarItemType.loadingButton,
        isError: isErrorUpdateAndIssueToTwd,
        isLoading: isLoadingUpdateAndIssueToTwd,
        isSuccess: isSuccessUpdateAndIssueToTwd,
        title: 'Issue to TWD',
        buttonIconName: 'EnvelopeCheck',
        onClick: onClickIssueTwd,
        componentType: ComponentType.Success,
        disable: isAnyActionLoading,
      });
    }
    if (
      isAllowCreateRevision &&
      designBriefDetail?.status === EDesignBriefStatus.FINISHED &&
      !masterSchedule?.isCancelled
    ) {
      items.push({
        type: EDetailPageTopBarItemType.loadingButton,
        isError: isErrorCreateRevision,
        isLoading: isLoadingCreateRevision,
        isSuccess: isSuccessCreateRevision,
        title: 'Create Revision',
        buttonIconName: 'Stickies',
        onClick: onClickCreateRevision,
        componentType: ComponentType.Info,
        disable: isAnyActionLoading,
      });
    }

    if (designBriefDetail?.status)
      items.push(
        <DownloadButton
          disabled={isAnyActionLoading}
          key="printPdf"
          exportFunction={downloadDesignBriefPdfAsync}
          defaultExportProps={{
            projectId,
            id: designBriefId as number,
          }}
          buttonRender={(exportFile) => (
            <Button prefixIconName="Printer" type={ComponentType.Secondary} onClick={() => exportFile()}>
              Print
            </Button>
          )}
        />,
      );

    return items;
  }, [
    mode,
    isAnyActionLoading,
    isErrorCreate,
    isLoadingCreate,
    isSuccessCreate,
    isLoadingUpdate,
    isErrorUpdate,
    isSuccessUpdate,
    isLoadingCreateAndIssueToTwc,
    isErrorCreateAndIssueToTwc,
    isSuccessCreateAndIssueToTwc,
    isLoadingUpdateAndIssueToTwc,
    isErrorUpdateAndIssueToTwc,
    isSuccessUpdateAndIssueToTwc,
    isLoadingUpdateAndIssueToTwd,
    isErrorUpdateAndIssueToTwd,
    isSuccessUpdateAndIssueToTwd,
    isLoadingCreateRevision,
    isErrorCreateRevision,
    isSuccessCreateRevision,
    isUploading,
    isAllowIssueToTwd,
    isAllowNewT2AndIssueToTwc,
    isAllowCreateRevision,
    JSON.stringify(designBriefDetail),
    JSON.stringify(masterSchedule),
  ]);

  const topBarRightItems = React.useMemo(() => {
    return [];
  }, [designBriefDetail?.status]);

  const [designerAttachmentCount, setDesignerAttachmentCount] = React.useState(0);

  return (
    <>
      <ApiSuccessToast
        successes={[
          { isSuccess: isSuccessCreate, message: 'Create design brief success.', reset: resetCreate },
          {
            isSuccess: isSuccessCreateAndIssueToTwc,
            message: 'Create and issue design brief to TWC success.',
            reset: resetCreateAndIssueToTwc,
          },
          { isSuccess: isSuccessUpdate, message: 'Update design brief success.', reset: resetUpdate },
          {
            isSuccess: isSuccessUpdateAndIssueToTwc,
            message: 'Update and issue design brief to TWC success.',
            reset: resetUpdateAndIssueToTwc,
          },
          {
            isSuccess: isSuccessUpdateAndIssueToTwd,
            message: 'Update and issue design brief to TWD success.',
            reset: resetUpdateAndIssueToTwd,
          },
          {
            isSuccess: isSuccessCreateRevision,
            message: 'Create design brief revision success.',
            reset: resetCreateRevision,
          },
        ]}
        callbackAfterToasted={() => {
          queryClient.invalidateQueries([projectId, designBriefDetail?.id, FETCH_QUERIES_NAME.DESIGN_BRIEF_DETAIL]);
          useFormMethods.reset(useFormMethods.getValues());

          if (isSuccessCreate) {
            setTimeout(() => {
              navigate(routes.t2.view.path, { id: dataCreate?.item?.id?.toString() });
            }, 0);
            return;
          }
          if (isSuccessCreateAndIssueToTwc) {
            setTimeout(() => {
              navigate(routes.t2.view.path, { id: dataCreateAndIssueToTwc?.item?.id?.toString() });
            }, 0);
          }
          if (isSuccessCreateRevision) {
            queryClient.invalidateQueries([projectId, masterScheduleId, FETCH_QUERIES_NAME.DESIGN_BRIEF_BY_T1]);
            queryClient.invalidateQueries([projectId, designBriefDetail?.id, FETCH_QUERIES_NAME.DESIGN_BRIEF_DETAIL]);
            useFormMethods.reset({}, { keepDirty: false });
            navigate(routes.t2.view.path, { id: dataCreateRevision?.item?.id?.toString() });
          }
        }}
      />
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          { error: errorCreate, genericError: 'Create design brief failed.', reset: resetCreate },
          { error: errorUpdate, genericError: 'Update design brief failed.', reset: resetUpdate },
          {
            error: errorCreateAndIssueToTwc,
            genericError: 'Create and issue to TWC failed.',
            reset: resetCreateAndIssueToTwc,
          },
          {
            error: errorCreateRevision,
            genericError: 'Create revision failed.',
            reset: resetCreateRevision,
          },
          {
            error: errorUpdateAndIssueToTwc,
            genericError: 'Update and issue to TWC failed.',
            reset: resetUpdateAndIssueToTwc,
          },
          {
            error: errorUpdateAndIssueToTwd,
            genericError: 'Update and issue to TWD failed.',
            reset: resetUpdateAndIssueToTwd,
          },
        ]}
      />
      <DetailPageTopBar leftItems={topBarLeftItems} rightItems={topBarRightItems} isReadOnly={isReadOnly} />

      <>
        <Grid className="gcl-mb-md">
          <Label labelFor={EDesignBriefFormKeys.RISK_CATEGORY} className="gcl-gems-design-brief-detail-label" required>
            Risk Category
          </Label>
          <FormTextField
            rules={requiredRule}
            disabled
            id={EDesignBriefFormKeys.RISK_CATEGORY}
            formFieldMethods={useFormMethods}
          />
          {
            (EDesignBriefFormKeys.RISK_CATEGORY,
            masterSchedule?.riskCategory?.category === ERiskCategory.A && (
              <Note
                className="gcl-fb-md gcl-mt-sm"
                componentType={ComponentType.Danger}
                prefixIcon={<Icon iconName={'ExclamationCircle' as IconName} />}
              >
                Risk Category A design package requires a workshop with the TWD and Operations Personnel. Please record
                the workshop by uploading the workshop notes to the attachments of this T2 or the comments tab.
              </Note>
            ))
          }
        </Grid>
        <Grid className="gcl-mb-md">
          <Label labelFor={EDesignBriefFormKeys.DESIGNER} className="gcl-gems-design-brief-detail-label" required>
            Designer
          </Label>
          <FormSelect
            displayFieldName="name"
            options={[]}
            rules={requiredRule}
            disabled
            id={EDesignBriefFormKeys.DESIGNER}
            formFieldMethods={useFormMethods}
          />
          {designBriefDetail?.carryOutDesignerCompanyId !== masterSchedule?.responsibleDesigner?.id &&
            (designBriefDetail?.status === EDesignBriefStatus.FINISHED ||
              designBriefDetail?.status === EDesignBriefStatus.DESIGNER_RECEIVED) && (
              <Note
                className="gcl-fb-md gcl-mt-sm"
                componentType={ComponentType.Danger}
                prefixIcon={<Icon iconName={'ExclamationCircle' as IconName} />}
              >
                This T2 was issued to previous designer, please revise and reissue.
              </Note>
            )}
        </Grid>
        <Grid className="gcl-mb-md">
          <Label
            required
            labelFor={EDesignBriefFormKeys.DESC_OF_WORKS_TO_BE_DESIGNED}
            className="gcl-gems-design-brief-detail-label"
          >
            <div className="gcl-mb-sm">
              Brief description of the works to be designed
              <Popover
                id="briefDescriptionPopover"
                overlay={
                  <>
                    To request checking by Lambeth, use a Site Transmittal for formal ICE Approval or informal Internal
                    Review.
                  </>
                }
              >
                <Icon iconName="QuestionCircle" className="gcl-ml-sm" />
              </Popover>
            </div>
          </Label>
          <FormTextareaWithMaxLength
            defaultNumOfLines={5}
            rules={requiredRule}
            disabled={isReadOnly}
            max={1200}
            id={EDesignBriefFormKeys.DESC_OF_WORKS_TO_BE_DESIGNED}
            formFieldMethods={useFormMethods}
          />
        </Grid>
        <Grid className="gcl-mb-md">
          <Label
            labelFor={EDesignBriefFormKeys.DESC_OF_WORKS_TO_BE_DESIGNED}
            className="gcl-gems-design-brief-detail-label"
          >
            <div className="gcl-mb-sm">
              Information attached to facilitate the design
              <Popover
                id="infoAttachedPopover"
                overlay={
                  <>
                    All Information necessary to enable Designer to produce the design without need to request further
                    details should be included.
                  </>
                }
              >
                <Icon iconName="QuestionCircle" className="gcl-ml-sm" />
              </Popover>
            </div>
          </Label>
          <Accordion
            allowClickHeaderToExpand
            className="gcl-mb-md"
            content={{
              id: 'infoAttachedPopoverAccordion',
              onRenderBody: () => {
                return (
                  <DesignBriefDetailInfoAttached
                    informationAttached={infoAttachedOptions ?? []}
                    formFieldMethods={useFormMethods}
                    isReadOnly={isReadOnly}
                    informationAttachedSelected={designBriefDetail?.infoAttached ?? []}
                  />
                );
              },
              defaultIsExpanded: true,
            }}
          />
        </Grid>
        <Grid className="gcl-mb-md">
          <LabelWithMessage required labelFor={EDesignBriefFormKeys.FILES} text="Attachments" />
          <Attachments<IProjectFileAttachmentsDesignBriefsRequest>
            setFileCount={setDesignerAttachmentCount}
            fileType={EProjectFileType.T2_ATTACHMENT}
            mode={modeAttachment}
            id={EDesignBriefFormKeys.FILES}
            uploadFileRequestParam={{ designBriefId }}
            useFetchFilesQueryKey={[
              projectId,
              designBriefId,
              FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_DESIGN_BRIEFS,
              EProjectFileType.T2_ATTACHMENT,
            ]}
            useFetchFiles={useFetchAttachmentDesignBrief}
            useFetchFilesProps={{
              designBriefId: designBriefId ?? 0,
              projectId,
              fileType: EProjectFileType.T2_ATTACHMENT,
            }}
            customHeaderButtons={[
              <Tooltip
                overlay="Download all files as zip"
                id="download-designer-attachment"
                key="download-designer-attachment"
              >
                {designerAttachmentCount > 0 ? (
                  <DownloadButton
                    exportFunction={downloadAttachmentsAsync}
                    defaultExportProps={{
                      projectId,
                      designBriefId,
                      fileType: EProjectFileType.T2_ATTACHMENT,
                    }}
                    buttonRender={(exportFile) => (
                      <Icon
                        onClick={() => exportFile()}
                        iconName="FileZip"
                        className="gcl-fc-primary-400 gcl-ic-btn-lg"
                      />
                    )}
                    onError={(errors) => {
                      toastMessage(
                        <ToastMessage
                          message={errors.errorMessage ?? 'Failed to download file'}
                          toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
                        />,
                      );
                    }}
                  />
                ) : (
                  <></>
                )}
              </Tooltip>,
            ]}
            formFieldMethods={useFormMethods}
            formKeyToUpdateInCreateMode={EDesignBriefFormKeys.FILES}
            formKeyToUpdateInUpdateMode={EDesignBriefFormKeys.FILES}
          />
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SignatureCard
              title="Initiated by SRP"
              signProfile={designBriefDetail?.initiatedProjectUserProfile?.userProfile?.signatureUserProfile}
              signUser={designBriefDetail?.initiatedProjectUserProfile?.userProfile?.displayName}
              signDate={convertUtcDateToTimezoneString(designBriefDetail?.initiatedDate)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SignatureCard
              title="Issued by TWC"
              signProfile={designBriefDetail?.receivedTwcProjectUserProfile?.userProfile?.signatureUserProfile}
              signUser={designBriefDetail?.receivedTwcProjectUserProfile?.userProfile?.displayName}
              signDate={convertUtcDateToTimezoneString(designBriefDetail?.receivedTwcDate)}
            />
          </Grid>
        </Grid>
      </>
    </>
  );
};
